/** Money is integer pesewas everywhere; these helpers are the only place it becomes text or comes back from a form. */
export function formatGhs(pesewas: number): string {
  const sign = pesewas < 0 ? '-' : '';
  const abs = Math.abs(Math.round(pesewas));
  const cedis = Math.trunc(abs / 100);
  const rest = String(abs % 100).padStart(2, '0');
  return `${sign}GH₵${cedis.toLocaleString('en-GH')}.${rest}`;
}

/** "250" or "250.50" -> 25000 / 25050 pesewas. Returns null for anything else (no guessing). */
export function parseCedis(input: string): number | null {
  const t = input.trim().replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [whole, frac = ''] = t.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

const DAY = 86_400_000;
const parse = (d: string) => Date.UTC(Number(d.slice(0, 4)), Number(d.slice(5, 7)) - 1, Number(d.slice(8, 10)));

export function addDays(date: string, days: number): string {
  const d = new Date(parse(date) + days * DAY);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

export function nightsBetween(a: string, b: string): number {
  return Math.round((parse(b) - parse(a)) / DAY);
}

/** "Sun 1 Mar" (adds the year when it is not the current year). */
export function formatDate(date: string, withYear = false): string {
  const d = new Date(parse(date));
  const base = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  return withYear ? `${base} ${d.getUTCFullYear()}` : base;
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'brand' | '';

export const BOOKING_STATUS: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: 'Held', tone: 'warn' },
  CONFIRMED: { label: 'Confirmed', tone: 'info' },
  CHECKED_IN: { label: 'In house', tone: 'good' },
  CHECKED_OUT: { label: 'Checked out', tone: '' },
  CANCELLED: { label: 'Cancelled', tone: 'bad' },
  NO_SHOW: { label: 'No-show', tone: 'bad' },
};

export const ROOM_STATUS: Record<string, { label: string; tone: Tone }> = {
  VACANT_CLEAN: { label: 'Clean', tone: 'good' },
  INSPECTED: { label: 'Inspected', tone: 'good' },
  VACANT_DIRTY: { label: 'Dirty', tone: 'warn' },
  OCCUPIED: { label: 'Occupied', tone: 'info' },
  OUT_OF_ORDER: { label: 'Out of order', tone: 'bad' },
};

export const DEPARTMENTS = ['ROOM', 'FOOD', 'BEVERAGE', 'LAUNDRY', 'HALL', 'POOL', 'TRANSPORT', 'OTHER'] as const;
export const DEPARTMENT_LABEL: Record<string, string> = {
  ROOM: 'Room',
  FOOD: 'Food',
  BEVERAGE: 'Beverage',
  LAUNDRY: 'Laundry',
  HALL: 'Hall',
  POOL: 'Pool',
  TRANSPORT: 'Transport',
  OTHER: 'Other',
  PAYMENT: 'Payment',
};

/** What a clock in `timezone` reads at a given instant, as milliseconds since the epoch (so the difference is the zone's offset). */
function wallClockMs(utcMs: number, timezone: string): number {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: timezone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(utcMs).map((x) => [x.type, x.value]));
  return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
}

/**
 * "2026-10-01T14:30" typed by staff means 14:30 at the hotel, wherever the phone or laptop thinks it is.
 * Returns the matching instant as an ISO string.
 */
export function hotelLocalToIso(local: string, timezone: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) throw new Error('Use a date and time');
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  let guess = asUtc;
  for (let i = 0; i < 2; i++) guess = asUtc - (wallClockMs(guess, timezone) - guess);
  return new Date(guess).toISOString();
}

/** "Thu 1 Oct, 14:30" at the hotel. */
export function formatHotelTime(iso: string, timezone: string): string {
  return new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: timezone });
}

/** The hotel-local calendar date (YYYY-MM-DD) of an instant. */
export function hotelDateOf(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));
}
